var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Paper, Popper, CircularProgress, useTheme, TextField, Autocomplete, } from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import { CountryCodes, getCountryFromAlpha2Code, getCountryFromAlpha3Code, showValidationMeta, SiteContext, } from '../../utils/common';
import Icon from '../Icon/Icon';
import useStyles from '../AddressLookup/AddressLookup.styles';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import axios from 'axios';
import clsx from 'clsx';
import { FormikContext, useField } from 'formik';
import _debounce from 'lodash/debounce';
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { noAddressFoundText, addressOptionSelectionErrorText, invalidAddressSelectionErrorText, errorDuringFetchingText, ieDataSet, gbDataSet, requiredText, optionalText, } from './constants';
const CustomPaperComponent = ({ children, classes }) => {
    return (_jsx(Paper, { className: classes.paper, square: true, children: children }));
};
const CustomPopperComponent = ({ open, boundariesElement, addressResponseLength, containerRef, isLargerViewport, classes = {}, props, }) => {
    return (_jsx(Popper, Object.assign({}, props, { open: open, modifiers: [
            { name: 'flip', enabled: true },
            {
                name: 'preventOverflow',
                enabled: true,
                options: {
                    boundary: boundariesElement !== null && boundariesElement !== void 0 ? boundariesElement : 'viewport',
                },
            },
        ], anchorEl: containerRef.current, className: clsx(classes.popper, addressResponseLength && classes.popperOpen), disablePortal: true, placement: isLargerViewport ? 'top' : 'bottom' })));
};
const CustomTextFieldComponent = ({ value, loading, inputRef, placeholder, handleTextFieldChange, handleOnFocus, handleKeyDown, params, }) => {
    var _a;
    return (_jsx(TextField, Object.assign({}, params, { fullWidth: true, value: value, variant: "standard", InputProps: Object.assign(Object.assign({}, params.InputProps), { autoComplete: 'off', 'aria-autocomplete': 'list', 'aria-haspopup': 'true', disableUnderline: true, disabled: loading, endAdornment: loading ? (_jsx(CircularProgress, { size: 20 })) : (_jsx(React.Fragment, { children: (_a = params.InputProps) === null || _a === void 0 ? void 0 : _a.endAdornment })) }), inputRef: inputRef, placeholder: placeholder, onChange: handleTextFieldChange, onFocus: handleOnFocus, onKeyDown: handleKeyDown })));
};
export const SuggestionItem = ({ option, inputValue, classes, rest, isIreland }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const county = isIreland ? (_b = (_a = option === null || option === void 0 ? void 0 : option.locality) === null || _a === void 0 ? void 0 : _a.region) === null || _b === void 0 ? void 0 : _b.name : (_d = (_c = option === null || option === void 0 ? void 0 : option.locality) === null || _c === void 0 ? void 0 : _c.sub_region) === null || _d === void 0 ? void 0 : _d.name;
    const city = (_f = (_e = option === null || option === void 0 ? void 0 : option.locality) === null || _e === void 0 ? void 0 : _e.town) === null || _f === void 0 ? void 0 : _f.name;
    const postalCode = (_g = option === null || option === void 0 ? void 0 : option.postal_code) === null || _g === void 0 ? void 0 : _g.full_name;
    if (!(county && city && (isIreland || postalCode)))
        return null;
    const suggestion = `${county}, ${city} ${postalCode !== null && postalCode !== void 0 ? postalCode : ''}`;
    const matches = match(suggestion, inputValue);
    const parts = parse(suggestion, matches);
    return (_jsx("li", Object.assign({}, rest, { className: classes.option, children: _jsx("div", { children: parts.map((part, index) => (_jsx("span", { style: { fontWeight: part.highlight ? 700 : 400 }, children: part.text }, `${index}+${part.text}`))) }) })));
};
const AddressLookupWrapperCNC = (props) => {
    // check if formik is available
    const formikContext = useContext(FormikContext);
    return formikContext ? _jsx(FormikAddressLookup, Object.assign({}, props, { formikContext: formikContext })) : _jsx(AddressLookup, Object.assign({}, props));
};
const FormikAddressLookup = (props) => {
    const [field, meta, helpers] = useField(props.name);
    const validation = showValidationMeta(meta);
    return _jsx(AddressLookup, Object.assign({ setAddressValue: helpers.setValue }, field, props, validation));
};
function createLookupObject(alpha3CountryCode, dataSets, query, lookupType, maxSuggestions) {
    return {
        country_iso: alpha3CountryCode,
        datasets: dataSets,
        key: {
            type: lookupType,
            value: query,
        },
        max_suggestions: maxSuggestions !== null && maxSuggestions !== void 0 ? maxSuggestions : 100,
    };
}
const AddressLookup = (_a) => {
    var _b;
    var { id, name, label, onChange, onClick, onFocus, handleClearAddress, className, boundariesElement, initialValue, countryCode, countryList, isFromPCC, setCurrentStep, hasVariants, getAddressLatLongDetails, maxSuggestions, fullWidth, required, placeholder, value, completed, completedDefault, disabled, state, fieldContainer = '', formikContext, addressFields, hideRequiredOptional, setAddressValue } = _a, textInputProps = __rest(_a, ["id", "name", "label", "onChange", "onClick", "onFocus", "handleClearAddress", "className", "boundariesElement", "initialValue", "countryCode", "countryList", "isFromPCC", "setCurrentStep", "hasVariants", "getAddressLatLongDetails", "maxSuggestions", "fullWidth", "required", "placeholder", "value", "completed", "completedDefault", "disabled", "state", "fieldContainer", "formikContext", "addressFields", "hideRequiredOptional", "setAddressValue"]);
    const [addressResponse, setAddressResponse] = useState([]);
    const [resultsOpen, setResultsOpen] = useState(false);
    const [optionSelectedState, setOptionSelectedState] = useState(false);
    const optionSelected = formikContext ? formikContext.values.optionSelected : optionSelectedState;
    const setOptionSelected = (isSelected) => formikContext ? formikContext.setFieldValue('optionSelected', isSelected) : setOptionSelectedState(isSelected);
    const [loading, setLoading] = useState(false);
    const authTokenHeader = {
        headers: {
            'Auth-Token': process.env.NEXT_PUBLIC_CNC_ADDRESS_LOOKUP_AUTH_TOKEN,
        },
    };
    const [height, setHeight] = useState();
    const { classes } = useStyles({ height });
    const containerRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const fieldId = id !== null && id !== void 0 ? id : name;
    const theme = useTheme();
    const { shorthand, countryCode: siteCountryCode } = useContext(SiteContext);
    const isIreland = shorthand === 'ie';
    const alpha3CountryCode = countryCode !== null && countryCode !== void 0 ? countryCode : getCountryFromAlpha2Code(siteCountryCode !== null && siteCountryCode !== void 0 ? siteCountryCode : CountryCodes.UNITED_KINDOM).codeAlpha3;
    const alpha2CountryCode = getCountryFromAlpha2Code(siteCountryCode !== null && siteCountryCode !== void 0 ? siteCountryCode : CountryCodes.UNITED_KINDOM).codeAlpha2;
    const isLargerViewport = useMediaQuery({ query: `(min-width: ${theme.breakpoints.values.md}px)` });
    const [inputValue, setInputValue] = useState('');
    const [onLoadCheck, setOnLoadCheck] = useState(true);
    const fieldError = formikContext === null || formikContext === void 0 ? void 0 : formikContext.errors[fieldId];
    const { [fieldId]: fieldIdValue = '', originalAddressSelection } = (_b = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) !== null && _b !== void 0 ? _b : {};
    const showFieldError = fieldError && ((formikContext === null || formikContext === void 0 ? void 0 : formikContext.touched[fieldId]) || (fieldContainer && (formikContext === null || formikContext === void 0 ? void 0 : formikContext.touched[fieldContainer])));
    const componentHasRendered = useRef(false);
    const requestLookupResponse = (query) => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d, _e, _f;
        const dataSets = alpha3CountryCode === 'IRL' ? [ieDataSet] : [gbDataSet];
        try {
            const lookupPostCallBodyForLocality = createLookupObject(alpha3CountryCode, dataSets, query, 'locality', maxSuggestions);
            const localityResponse = yield axios.post(`${process.env.NEXT_PUBLIC_CNC_ADDRESS_LOOKUP_URL}`, lookupPostCallBodyForLocality, authTokenHeader);
            const lookupPostCallBodyForPostCode = createLookupObject(alpha3CountryCode, dataSets, query, 'postal_code', maxSuggestions);
            const postCodeResponse = yield axios.post(`${process.env.NEXT_PUBLIC_CNC_ADDRESS_LOOKUP_URL}`, lookupPostCallBodyForPostCode, authTokenHeader);
            let unfilteredCombinedSuggestions = [
                ...(Array.isArray((_d = (_c = localityResponse === null || localityResponse === void 0 ? void 0 : localityResponse.data) === null || _c === void 0 ? void 0 : _c.result) === null || _d === void 0 ? void 0 : _d.suggestions) ? localityResponse.data.result.suggestions : []),
                ...(Array.isArray((_f = (_e = postCodeResponse === null || postCodeResponse === void 0 ? void 0 : postCodeResponse.data) === null || _e === void 0 ? void 0 : _e.result) === null || _f === void 0 ? void 0 : _f.suggestions) ? postCodeResponse.data.result.suggestions : []),
            ];
            // Filter out duplicates
            const combinedSuggestions = unfilteredCombinedSuggestions.filter((suggestion, index, self) => index ===
                self.findIndex((s) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
                    return ((_b = (_a = s.locality) === null || _a === void 0 ? void 0 : _a.town) === null || _b === void 0 ? void 0 : _b.name) === ((_d = (_c = suggestion.locality) === null || _c === void 0 ? void 0 : _c.town) === null || _d === void 0 ? void 0 : _d.name) &&
                        ((_e = s.postal_code) === null || _e === void 0 ? void 0 : _e.full_name) === ((_f = suggestion.postal_code) === null || _f === void 0 ? void 0 : _f.full_name) &&
                        (isIreland
                            ? ((_h = (_g = s.locality) === null || _g === void 0 ? void 0 : _g.region) === null || _h === void 0 ? void 0 : _h.name) === ((_k = (_j = suggestion.locality) === null || _j === void 0 ? void 0 : _j.region) === null || _k === void 0 ? void 0 : _k.name)
                            : ((_m = (_l = s.locality) === null || _l === void 0 ? void 0 : _l.sub_region) === null || _m === void 0 ? void 0 : _m.name) === ((_p = (_o = suggestion.locality) === null || _o === void 0 ? void 0 : _o.sub_region) === null || _p === void 0 ? void 0 : _p.name));
                }));
            if (initialValue && query === initialValue && combinedSuggestions.length) {
                setOptionSelected(true);
                handleLookupSelection(combinedSuggestions[0]);
            }
            else if (combinedSuggestions.length) {
                setAddressResponse(combinedSuggestions);
                setResultsOpen(true);
            }
        }
        catch (error) {
            console.error(errorDuringFetchingText, error);
        }
    });
    const debouncedLookup = useMemo(() => _debounce((query) => requestLookupResponse(inputValue), 300), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]);
    useEffect(() => {
        return () => {
            debouncedLookup.cancel();
        };
    }, [debouncedLookup]);
    useEffect(() => {
        const cleanValue = value ? value.replace(/(\r\n|\n|\r)/gm, ' ') : '';
        if (typeof value == 'string' && cleanValue.length > 2 && !optionSelected) {
            debouncedLookup(cleanValue);
        }
        else if (optionSelected) {
            setResultsOpen(false);
        }
        else if (!cleanValue) {
            setAddressResponse([]);
        }
        if (!componentHasRendered.current) {
            componentHasRendered.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, optionSelected]);
    useEffect(() => {
        var _a;
        if (onLoadCheck) {
            onLoadCheck && setOnLoadCheck(false);
            return;
        }
        const isValid = optionSelected && originalAddressSelection;
        if (isValid) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
            formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldError(fieldId, '');
            formikContext === null || formikContext === void 0 ? void 0 : formikContext.validateForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultsOpen, originalAddressSelection, optionSelected, fieldContainer, fieldIdValue]);
    useEffect(() => {
        setInputValue('');
    }, [countryCode]);
    useEffect(() => {
        const outsideFocus = (event) => {
            var _a;
            if (resultsOpen && event.relatedTarget && !((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.relatedTarget))) {
                setResultsOpen(false);
                formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldTouched(name, true);
            }
        };
        const ref = containerRef.current;
        if (isLargerViewport) {
            ref === null || ref === void 0 ? void 0 : ref.addEventListener('focusout', outsideFocus);
            return () => {
                ref === null || ref === void 0 ? void 0 : ref.removeEventListener('focusout', outsideFocus);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef, resultsOpen]);
    const handleLookupSelection = (addressResponse) => __awaiter(void 0, void 0, void 0, function* () {
        var _g, _h, _j, _k, _l, _m, _o, _p, _q;
        try {
            setLoading(true);
            // we are not storing the county(sub_region) field in the formik context because the county value we get from experian is different from the one we use in rose.
            const addressFields = {
                country: alpha2CountryCode,
                city: (_h = (_g = addressResponse === null || addressResponse === void 0 ? void 0 : addressResponse.locality) === null || _g === void 0 ? void 0 : _g.town) === null || _h === void 0 ? void 0 : _h.name,
                postalCode: (_k = (_j = addressResponse === null || addressResponse === void 0 ? void 0 : addressResponse.postal_code) === null || _j === void 0 ? void 0 : _j.full_name) !== null && _k !== void 0 ? _k : '',
            };
            if (addressFields.country && addressFields.city && (isIreland || addressFields.postalCode)) {
                formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue(textFieldName('shouldValidatePostCode'), ((_l = getCountryFromAlpha3Code(countryCode)) === null || _l === void 0 ? void 0 : _l.name) === CountryCodes.UNITED_KINDOM);
                Object.entries(addressFields).forEach(([addressField, addressValue]) => {
                    const locatedFieldName = fieldContainer ? `${fieldContainer}.${addressField}` : addressField;
                    formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue(locatedFieldName, addressValue);
                    formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldTouched(locatedFieldName, true);
                    if (fieldContainer) {
                        formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldTouched(fieldContainer, true);
                    }
                });
                const suggestion = `${isIreland ? (_o = (_m = addressResponse === null || addressResponse === void 0 ? void 0 : addressResponse.locality) === null || _m === void 0 ? void 0 : _m.region) === null || _o === void 0 ? void 0 : _o.name : (_q = (_p = addressResponse === null || addressResponse === void 0 ? void 0 : addressResponse.locality) === null || _p === void 0 ? void 0 : _p.sub_region) === null || _q === void 0 ? void 0 : _q.name}, ${addressFields.city} ${addressFields.postalCode}`;
                setAddressValue ? setAddressValue(suggestion) : onChange && onChange({ target: { value: suggestion } });
                formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('originalAddressSelection', addressFields.postalCode || addressFields.city);
                formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('collectionAddress', addressFields.postalCode || addressFields.city);
                getAddressLatLongDetails === null || getAddressLatLongDetails === void 0 ? void 0 : getAddressLatLongDetails(addressFields);
                if (isFromPCC && setCurrentStep)
                    setCurrentStep();
            }
            else {
                setInputValue('');
                throw new Error(invalidAddressSelectionErrorText);
            }
        }
        catch (error) {
            console.error(addressOptionSelectionErrorText, error);
            formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldError(fieldId, addressOptionSelectionErrorText);
        }
        finally {
            setLoading(false);
            setResultsOpen(false);
            setAddressResponse([]);
        }
    });
    const textFieldName = (addressLine) => (fieldContainer ? `${fieldContainer}.${addressLine}` : addressLine);
    const clearAllHiddenFields = (ignoreCountry) => {
        addressFields.forEach((fieldName) => {
            if (fieldName === 'country' && ((countryList && countryList.length === 1) || ignoreCountry)) {
                return;
            }
            const locatedFieldName = fieldContainer ? `${fieldContainer}.${fieldName}` : fieldName;
            formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue(locatedFieldName, '', false);
            formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldTouched(locatedFieldName, false, false);
            if (fieldContainer) {
                formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldTouched(fieldContainer, false, false);
            }
        });
        handleClearAddress && handleClearAddress();
    };
    const handleAddressSelection = (addressResponse) => {
        if (addressResponse) {
            setOptionSelected(true);
            handleLookupSelection(addressResponse);
            formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldError(fieldId, '');
        }
        else {
            clearAllHiddenFields();
            setAddressResponse([]);
            formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue(fieldId, '');
        }
    };
    const handleOnFocus = (e) => {
        setResultsOpen(true);
        onFocus && onFocus(e);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };
    const handleTextFieldChange = (e) => {
        setInputValue(e.target.value);
        onChange && onChange(e);
        setOptionSelected(false);
    };
    useEffect(() => {
        var _a;
        const outsideClick = (event) => {
            var _a;
            if (resultsOpen && !((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
                setResultsOpen(false);
                formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldTouched(name, true);
            }
        };
        const modalContent = document.querySelector('.MuiDialogContent-root');
        const modalContentHeight = modalContent === null || modalContent === void 0 ? void 0 : modalContent.clientHeight;
        document.body.addEventListener('click', outsideClick);
        if (!hasVariants && modalContentHeight && resultsOpen && !((_a = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _a === void 0 ? void 0 : _a.showManualAddress)) {
            let scale = isLargerViewport ? 0.33 : null;
            if (scale)
                setHeight(modalContentHeight * scale);
        }
        return () => {
            document.body.removeEventListener('click', outsideClick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultsOpen]);
    const textFieldErrorElement = showFieldError && _jsx("span", { className: classes.errorMessage, children: fieldError.toString() });
    const editIcon = (_jsxs(_Fragment, { children: [_jsx("span", { className: classes.textContent, children: value !== null && value !== void 0 ? value : completedDefault }), _jsx(Icon, { icon: "create", fontSize: "inherit", className: classes.editIcon })] }));
    const getRequiredOrOptionalText = () => {
        if (hideRequiredOptional)
            return '';
        if (required) {
            return requiredText;
        }
        return optionalText;
    };
    const requiredOrOptionalLabel = (_jsxs("label", { className: classes.label, htmlFor: fieldId, children: [`${label} `, getRequiredOrOptionalText()] }));
    // cannot move this wrapper outside of the component as it needs variables from this component
    const CustomPaperWrapper = useCallback((props) => _jsx(CustomPaperComponent, Object.assign({ classes: { paper: classes.paper } }, props)), [classes.paper]);
    const CustomPopperWrapper = useCallback((props) => (_jsx(CustomPopperComponent, { open: true, containerRef: containerRef, isLargerViewport: isLargerViewport, addressResponseLength: addressResponse.length, boundariesElement: boundariesElement, classes: {
            popper: classes.popper,
            popperOpen: classes.popperOpen,
        }, props: props })), [containerRef, isLargerViewport, addressResponse, boundariesElement, classes]);
    const containerClassName = clsx(classes.textField, state === 'error' || !!showFieldError ? classes.error : '', state === 'success' ? classes.success : '', disabled || loading ? classes.disabled : '', completed ? classes.completed : '', value ? classes.touched : '');
    return (_jsxs("div", { className: clsx(classes.textFieldWrapper, fullWidth ? classes.fullWidth : ''), children: [_jsx("div", Object.assign({ ref: containerRef, className: containerClassName }, (completed && { role: 'button', onClick }), { children: completed ? (editIcon) : (_jsx(Autocomplete, { id: fieldId, filterOptions: (x) => x, clearOnBlur: true, PopperComponent: CustomPopperWrapper, PaperComponent: CustomPaperWrapper, noOptionsText: noAddressFoundText, renderOption: (props, option, { inputValue }) => (_jsx(SuggestionItem, { option: option, inputValue: inputValue, rest: props, classes: classes, isIreland: isIreland })), open: resultsOpen, value: value !== null && value !== void 0 ? value : inputValue, freeSolo: true, onChange: (_, v) => {
                        handleAddressSelection(v);
                    }, onFocus: handleOnFocus, getOptionLabel: (option) => { var _a, _b; return (_b = (_a = option.suggestion) !== null && _a !== void 0 ? _a : value) !== null && _b !== void 0 ? _b : ''; }, loading: loading, disabled: loading, options: addressResponse, classes: {
                        popupIndicator: classes.hasPopupIcon,
                        root: classes.root,
                        endAdornment: classes.endAdornment,
                    }, renderInput: (params) => {
                        return (_jsxs("div", { className: classes.textContainer, children: [requiredOrOptionalLabel, _jsx(CustomTextFieldComponent, { value: value, loading: loading, inputRef: inputRef, placeholder: placeholder, handleTextFieldChange: handleTextFieldChange, handleOnFocus: handleOnFocus, handleKeyDown: handleKeyDown, params: params })] }));
                    } })) })), textFieldErrorElement] }));
};
export default AddressLookupWrapperCNC;
